import { Checkbox, TableFooter, TableHead } from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination";
import { maybe } from "@saleor/misc";
import RemittanceRunnerCheckbox from "@saleor/remittanceRunner/components/RemittanceRunnerCheckbox";
import { RemittanceRunnerOrders_remittanceOrders } from "@saleor/remittanceRunner/types/RemittanceRunnerOrders";
import { RemittanceMotherOrder } from "@saleor/remittanceRunner/utils/formDataTypes";
import { ListProps } from "@saleor/types";
import {
  RemittanceManualAdjustmentStatusEnum,
  RemittanceStatusEnum,
  SmopOrderStatusEnum
} from "@saleor/types/globalTypes";
import * as React from "react";
import { FormattedMessage } from "react-intl";

import { useTableStyles } from "../../styles/tableStyles";
import TableBodyContent from "./TableBodyContent";

interface RemittanceRunnerListNonDispatcherProp extends ListProps {
  remittances: RemittanceRunnerOrders_remittanceOrders;
  loadingOrders: boolean;
  disabled: boolean;
  isOmiAdmin: boolean;
  isSuperUser: boolean;
  userCanOverrideRemittance: boolean;
  remittanceIdList: string[] | null;
  setMotherOrderList: React.Dispatch<React.SetStateAction<string[]>>;
  setMotherOrderCode: React.Dispatch<
    React.SetStateAction<RemittanceMotherOrder>
  >;
  onRefetchOrders: () => void;
}

const dateFormat = "MMM DD, YYYY hh:mm A";

export const RemittanceRunnerTableNonDispatcher: React.FC<RemittanceRunnerListNonDispatcherProp> = props => {
  const {
    remittances,
    loadingOrders,
    disabled,
    isOmiAdmin,
    isSuperUser,
    userCanOverrideRemittance,
    settings,
    pageInfo,
    remittanceIdList,
    setMotherOrderList,
    setMotherOrderCode,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings,
    onRefetchOrders
  } = props;

  const numberOfColumns = isOmiAdmin || isSuperUser ? 12 : 11;
  const tblClasses = useTableStyles(props);

  const remittancesList = maybe(
    () => remittances.edges.map(edge => edge.node),
    []
  );

  const handleRemittanceSelected = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const isChecked = event.target.checked;

    if (isChecked) {
      const selectedIds = remittancesList
        .filter(
          remittance =>
            (remittance?.remittanceLine === null ||
              remittance?.remittanceLine?.remittance?.status ===
                RemittanceStatusEnum.TO_REMIT) &&
            remittance?.remittanceManualAdjustmentStatus !==
              RemittanceManualAdjustmentStatusEnum.FOR_ADJUSTMENT &&
            remittance?.smopStatus === SmopOrderStatusEnum.COMPLETED &&
            userCanOverrideRemittance
        )
        .map(remittance => remittance.id);

      setMotherOrderList(selectedIds);
    } else {
      setMotherOrderList([]);
    }
  };

  const validRemittanceIds =
    remittancesList.length > 0 &&
    remittancesList.every(
      remittance =>
        remittanceIdList.includes(remittance.id) ||
        remittance?.remittanceLine?.remittance?.status ===
          RemittanceStatusEnum.REMITTED ||
        remittance?.remittanceManualAdjustmentStatus ===
          RemittanceManualAdjustmentStatusEnum.FOR_ADJUSTMENT ||
        remittance?.smopStatus !== SmopOrderStatusEnum.COMPLETED ||
        !userCanOverrideRemittance
    );

  const validForBulkOverride = remittancesList.filter(
    remittance =>
      (remittance?.remittanceLine === null ||
        (remittance?.remittanceLine !== null &&
          remittance?.remittanceLine?.remittance?.status ===
            RemittanceStatusEnum.TO_REMIT)) &&
      remittance?.remittanceManualAdjustmentStatus !==
        RemittanceManualAdjustmentStatusEnum.FOR_ADJUSTMENT &&
      remittance?.smopStatus === SmopOrderStatusEnum.COMPLETED
  );

  return (
    <div className={tblClasses.responsiveTable}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCellHeader
              className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
            >
              <Checkbox
                className={tblClasses.remittanceCheckbox}
                checked={validRemittanceIds && validForBulkOverride.length > 0}
                icon={<RemittanceRunnerCheckbox />}
                disabled={
                  !userCanOverrideRemittance ||
                  validForBulkOverride.length === 0
                }
                onChange={handleRemittanceSelected}
              />
            </TableCellHeader>
            <TableCellHeader
              className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
            >
              <FormattedMessage
                defaultMessage="Mother Order"
                description="mother order code"
              />
            </TableCellHeader>
            <TableCellHeader
              className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
            >
              <FormattedMessage defaultMessage="Status" description="status" />
            </TableCellHeader>
            <TableCellHeader
              className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
            >
              <FormattedMessage
                defaultMessage="Remittance Date"
                description="remittance date"
              />
            </TableCellHeader>
            <TableCellHeader
              className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
            >
              <FormattedMessage
                defaultMessage="Remitter"
                description="remitter"
              />
            </TableCellHeader>
            <TableCellHeader
              className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
            >
              <FormattedMessage
                defaultMessage="Company Name"
                description="company name"
              />
            </TableCellHeader>
            <TableCellHeader
              className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
            >
              <FormattedMessage
                defaultMessage="Payment Type"
                description="payment type"
              />
            </TableCellHeader>
            <TableCellHeader
              className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
            >
              <FormattedMessage defaultMessage="Age" description="age" />
            </TableCellHeader>
            <TableCellHeader
              className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
            >
              <FormattedMessage
                defaultMessage="Remittance ID"
                description="remittance id"
              />
            </TableCellHeader>
            {(isOmiAdmin || isSuperUser) && (
              <TableCellHeader
                className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
              >
                <FormattedMessage defaultMessage="Mall" description="mall" />
              </TableCellHeader>
            )}
            <TableCellHeader
              className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
            >
              <FormattedMessage
                defaultMessage="Payment Collection"
                description="payment collection"
              />
            </TableCellHeader>
            <TableCellHeader
              className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
            >
              <FormattedMessage
                defaultMessage="Receipt Uploaded"
                description="receipt uploaded"
              />
            </TableCellHeader>
          </TableRow>
        </TableHead>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={numberOfColumns}
              settings={settings}
              hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
              onNextPage={onNextPage}
              onUpdateListSettings={onUpdateListSettings}
              hasPreviousPage={
                pageInfo && !disabled ? pageInfo.hasPreviousPage : false
              }
              onPreviousPage={onPreviousPage}
            />
          </TableRow>
        </TableFooter>

        <TableBody>
          <TableBodyContent
            tblClasses={tblClasses}
            numberOfColumns={numberOfColumns}
            dateFormat={dateFormat}
            isOmiAdmin={isOmiAdmin}
            isSuperUser={isSuperUser}
            userCanOverrideRemittance={userCanOverrideRemittance}
            loadingOrders={loadingOrders}
            remittancesList={remittancesList}
            remittanceIdList={remittanceIdList}
            setMotherOrderList={setMotherOrderList}
            setMotherOrderCode={setMotherOrderCode}
            onRefetchOrders={onRefetchOrders}
          />
        </TableBody>
      </Table>
    </div>
  );
};

export default RemittanceRunnerTableNonDispatcher;
